import type { PlanFeature, SelfServePlanKey } from "@giftforward/entitlements";
import { isSelfServePlan, PLAN_KEYS } from "@giftforward/entitlements";
import CheckIcon from "@mui/icons-material/Check";
import { PLAN_CARD_STRINGS } from "@/constants/Plans";
import { usePlans } from "@/hooks/usePlans";
import type { PlanTier } from "@/interfaces/tier";

/**
 * The pricing-tier cards, one per plan in PLAN_KEYS. Presentational only: the CTA matrix maps each card to a
 * callback and the parent decides what it does.
 *
 * Two variants share this component (DIV-7761):
 * - "account" (default, PRD F1.3-F1.4): Account > Plan and OrgDetail. Badges the CURRENT
 *   tier "Your plan".
 * - "upsell": the in-app plan modal opened from campaign-builder gates. Badges nothing — it sells
 *   up from wherever the caller is.
 *
 * Moving between two PAID plans has no CTA in either variant: the upgrade path creates a new Stripe
 * subscription and would leave the current one billing beside it.
 */
interface PlanTierCardsProps {
  /**
   * The organization's current plan, or undefined while the matrix is still resolving. Undefined
   * marks no card as current rather than guessing at Starter — which is what the old tier hook
   * did, and why a Pro client could see "Current plan" on the wrong card for a frame.
   */
  plan: PlanTier | undefined;
  variant?: "account" | "upsell";
  /** true = a downgrade is scheduled: the Starter card offers "Cancel downgrade" (PRD F1.4). Ignored in "upsell". */
  pendingDowngrade: boolean;
  /** A self-serve plan's buy CTA. The parent opens checkout for `plan`. */
  onSelectPlan: (plan: SelfServePlanKey) => void;
  onDowngrade: () => void;
  onCancelDowngrade: () => void;
  onTalkToSales: () => void;
  /** Disables every action button (impersonation / in-flight action). */
  disabled?: boolean;
}

/** A card's call-to-action area: a clickable button, a plain status label, or nothing. */
type CardCta =
  | { kind: "button"; label: string; onClick: () => void; primary: boolean }
  | { kind: "label"; label: string }
  | { kind: "none" };

// Black styling (bg/text/hover) matches the prototype's `.uplan-btn` rule.
const BUTTON_BASE_CLASSES =
  "inline-flex cursor-pointer items-center justify-center rounded-btn font-lato text-xs font-bold uppercase tracking-[1px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rodeo-black focus-visible:ring-offset-2 disabled:pointer-events-none disabled:cursor-default disabled:opacity-40";
const BUTTON_OUTLINED_CLASSES =
  "border border-rodeo-black bg-white text-rodeo-black px-4 py-3 hover:bg-rodeo-black hover:text-white";
const BUTTON_CONTAINED_CLASSES =
  "border border-rodeo-black bg-rodeo-black text-white px-4 py-3 hover:bg-[#222]";

const DIVIDER_CLASSES =
  "mb-1 border-b border-smoke pt-1.5 pb-2.5 text-xs font-bold uppercase tracking-[0.5px] text-dust-ink";

/**
 * Splits a plan's leading "Everything in X, plus" divider off its feature list: the leading divider
 * gets its own row track in the card (so it lines up across the row); any later divider stays
 * inline in the list.
 */
function splitLeadingDivider(features: readonly PlanFeature[]): {
  leadingDivider: string | null;
  listFeatures: readonly PlanFeature[];
} {
  const [first, ...rest] = features;
  return typeof first === "object"
    ? { leadingDivider: first.divider, listFeatures: rest }
    : { leadingDivider: null, listFeatures: features };
}

function TierFeatureList({ features }: { features: readonly PlanFeature[] }) {
  return (
    <ul className="m-0 list-none p-0">
      {features.map((feature) =>
        typeof feature === "object" ? (
          <li key={`divider-${feature.divider}`} className={DIVIDER_CLASSES}>
            {feature.divider}
          </li>
        ) : (
          <li
            key={feature}
            className="flex items-start gap-2.5 py-1.5 text-sm leading-[1.45] text-rodeo-black"
          >
            <CheckIcon className="mt-0.5 shrink-0 text-[18px] text-good-sage" />
            <span>{feature}</span>
          </li>
        ),
      )}
    </ul>
  );
}

function TierCard({
  tier,
  variant,
  isFeatured,
  cta,
  disabled,
}: {
  tier: PlanTier;
  variant: "account" | "upsell";
  /** account: the current tier. upsell: never. */
  isFeatured: boolean;
  cta: CardCta;
  disabled: boolean;
}) {
  const { byKey } = usePlans();
  const meta = byKey(tier)?.display;
  const isUpsell = variant === "upsell";

  // No catalogue entry means the plan is not being served — render nothing rather than a card
  // with no name or price. One guard instead of an optional access on every field below.
  if (!meta) return null;

  const shadowClass =
    isUpsell && isFeatured
      ? "shadow-[0_8px_24px_rgba(0,0,0,0.12)]"
      : "shadow-[0_1px_3px_rgba(0,0,0,0.04)]";
  const { leadingDivider, listFeatures } = splitLeadingDivider(meta.features);

  // The card is a row subgrid spanning five row tracks of the outer grid (name · price · CTA ·
  // divider · bullets), so each section's track is as tall as the tallest card's section in the
  // same visual row and the sections line up across cards. gap-y-0 cancels the row gap the subgrid
  // would otherwise inherit from the outer grid; spacing inside the card comes from the margins.
  // The 20px between the CTA and the divider/bullets is a top margin on the divider-track element,
  // not a bottom margin on the CTA: a track no card fills (a row of divider-less cards) cannot
  // absorb the outer gap, so that margin has to live inside the track to keep the spacing at 20px.
  return (
    <div
      className={`relative row-span-5 grid grid-cols-1 grid-rows-subgrid gap-y-0 rounded-card bg-white ${shadowClass} ${
        isFeatured
          ? "border-2 border-dark-dust px-[27px] py-[31px]"
          : `border ${isUpsell ? "border-card-border" : "border-smoke"} px-7 py-8`
      }`}
    >
      {isFeatured && (
        // Badge is absolutely positioned at -top-3.5; the grid needs clearance above the
        // row or it clips.
        <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-[100px] bg-dark-dust px-[18px] py-1.5 text-xs font-bold tracking-[1.2px] whitespace-nowrap uppercase text-white shadow-[0_4px_12px_rgba(175,149,119,0.35)]">
          {PLAN_CARD_STRINGS.yourPlanBadge}
        </div>
      )}

      {/* <h2>, not <h3>: on the plan modal / Plan tab this sits directly under the page/dialog
          <h1>/title, where an <h3> would skip a level (axe heading-order); in Account > Plan
          it is a valid sibling of the org <h2>. Not a plain <div> either — the tier names
          have to stay reachable by heading navigation. The base-layer `h2` tag rule
          (Crimson/24px/400/1.2/#333, index.css) is fully overridden by the classes below, so
          this renders unchanged. */}
      <h2
        className={`mb-2 font-lato font-bold uppercase text-dust-ink ${
          isUpsell ? "text-[20px] tracking-[1.5px]" : "text-[15px] leading-[1.6] tracking-[1.2px]"
        }`}
      >
        {meta.name}
      </h2>

      <div className="mb-[22px]">
        <span
          className={`font-crimson leading-none text-rodeo-black ${
            isUpsell ? "font-semibold" : ""
          } ${isUpsell && isFeatured ? "text-[48px]" : "text-4xl"}`}
        >
          {meta.amountLabel}
        </span>
        {meta.unit !== "" && (
          <span className="ml-1 font-crimson text-[22px] text-graphite">{meta.unit}</span>
        )}
        <p className="mt-2 text-[13px] text-graphite">{meta.billingLabel}</p>
      </div>

      <div className="mt-[2px]">
        {cta.kind === "button" ? (
          <button
            type="button"
            onClick={cta.onClick}
            disabled={disabled}
            className={`${BUTTON_BASE_CLASSES} w-full ${
              cta.primary ? BUTTON_CONTAINED_CLASSES : BUTTON_OUTLINED_CLASSES
            }`}
          >
            {cta.label}
          </button>
        ) : cta.kind === "label" ? (
          <p className="py-3 text-center text-xs font-bold uppercase tracking-[1px] text-graphite">
            {cta.label}
          </p>
        ) : (
          // Holds the button's height so the card keeps the spacing it has with a button.
          <div className="h-[42px]" />
        )}
      </div>

      {leadingDivider !== null ? (
        <p className={`mt-5 ${DIVIDER_CLASSES}`}>{leadingDivider}</p>
      ) : (
        // Fills the divider track so this card's bullets start level with its neighbours'.
        <div aria-hidden="true" className="mt-5" />
      )}

      <TierFeatureList features={listFeatures} />
    </div>
  );
}

export default function PlanTierCards({
  plan,
  variant = "account",
  pendingDowngrade,
  onSelectPlan,
  onDowngrade,
  onCancelDowngrade,
  onTalkToSales,
  disabled = false,
}: PlanTierCardsProps) {
  const { byKey } = usePlans();
  const currentIndex = plan ? PLAN_KEYS.indexOf(plan) : -1;
  // Already paying for a self-serve plan: the other one is not sold to them from here.
  const onPaidPlan = isSelfServePlan(plan);

  /** The buy button for a self-serve tier, or nothing when the caller already pays for one. */
  function buyCta(
    tier: SelfServePlanKey,
    label: (badge: string) => string,
    primary: boolean,
  ): CardCta {
    if (onPaidPlan) return { kind: "none" };
    return {
      kind: "button",
      label: label(byKey(tier)?.display.badgeLabel ?? ""),
      onClick: () => onSelectPlan(tier),
      primary,
    };
  }

  /** CTA matrix for Account > Plan / OrgDetail (PRD F1.4). */
  function ctaForAccount(tier: PlanTier): CardCta {
    if (tier === plan) return { kind: "label", label: PLAN_CARD_STRINGS.currentPlan };
    const primary = PLAN_KEYS.indexOf(tier) > currentIndex;
    if (tier === "enterprise") {
      return {
        kind: "button",
        label: PLAN_CARD_STRINGS.talkToSales,
        onClick: onTalkToSales,
        primary,
      };
    }
    if (tier === "starter") {
      return pendingDowngrade && onPaidPlan
        ? {
            kind: "button",
            label: PLAN_CARD_STRINGS.cancelDowngrade,
            onClick: onCancelDowngrade,
            primary,
          }
        : { kind: "button", label: PLAN_CARD_STRINGS.downgrade, onClick: onDowngrade, primary };
    }
    return buyCta(tier, PLAN_CARD_STRINGS.switchTo, primary);
  }

  /**
   * CTA matrix for the upsell modal. Tiers below the caller's current plan are already paid
   * for ("Included"); pendingDowngrade plays no role here — this modal only ever sells up.
   */
  function ctaForUpsell(tier: PlanTier): CardCta {
    if (tier === plan) return { kind: "label", label: PLAN_CARD_STRINGS.currentPlan };
    if (PLAN_KEYS.indexOf(tier) < currentIndex) {
      return { kind: "label", label: PLAN_CARD_STRINGS.included };
    }
    if (tier === "enterprise") {
      return {
        kind: "button",
        label: PLAN_CARD_STRINGS.talkToSales,
        onClick: onTalkToSales,
        primary: false,
      };
    }
    if (tier === "starter") return { kind: "none" };
    return buyCta(tier, PLAN_CARD_STRINGS.getPlan, true);
  }

  const ctaFor = variant === "upsell" ? ctaForUpsell : ctaForAccount;

  const gridClasses =
    variant === "upsell"
      ? // Narrow: single column capped at 460px, centered. Wide: two by two, then all four in a
        // row once the modal's container (max 992px inside its 1040px wrapper) can hold them.
        "grid grid-cols-1 items-stretch gap-5 max-w-[460px] mx-auto @[772px]:max-w-none @[772px]:mx-0 @[772px]:grid-cols-2 @[960px]:grid-cols-4"
      : "grid grid-cols-1 items-stretch gap-5 @xl:grid-cols-2 @5xl:grid-cols-4";

  return (
    <div className="@container">
      <div className={gridClasses}>
        {PLAN_KEYS.map((tier) => (
          <TierCard
            key={tier}
            tier={tier}
            variant={variant}
            isFeatured={variant === "account" && tier === plan}
            cta={ctaFor(tier)}
            disabled={disabled}
          />
        ))}
      </div>
    </div>
  );
}
